import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Calendar, Clock, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";
import { API_BASE } from "../lib/api";
const REKISA_LOGO = "https://customer-assets.emergentagent.com/job_growth-workshop/artifacts/5k7dsa9t_Rekisa%20white.png";
const THATO_IMAGE = "https://customer-assets.emergentagent.com/job_growth-workshop/artifacts/q1ocb4xe_Thato%20Image.jpg";

export default function ThankYou() {
  const [searchParams] = useSearchParams();
  const [orderData, setOrderData] = useState(null);
  const reference = searchParams.get("reference");
  
  useEffect(() => {
    if (reference) {
      fetchOrder();
    }
  }, [reference]);
  
  const fetchOrder = async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/order/${reference}`);
      setOrderData(response.data);
    } catch (error) {
      console.error("Failed to fetch order:", error);
    }
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-100">
      {/* Header */}
      <header className="py-6 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <Link to="/" className="inline-block">
            <div className="bg-slate-900 px-3 py-1.5 rounded-lg">
              <img src={REKISA_LOGO} alt="Rekisa" className="h-6" />
            </div>
          </Link>
        </div>
      </header>
      
      <main className="py-12 px-6">
        <div className="max-w-3xl mx-auto">
          {/* Success banner */}
          <div className="text-center mb-12" data-testid="thank-you-page">
            <div className="w-20 h-20 rounded-full bg-[#10B981]/10 flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-10 h-10 text-[#10B981]" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              You're Registered!
            </h1>
            <p className="text-xl text-slate-600">
              Thank you for joining the Growth Marketing Workshop.
            </p>
          </div>
          
          {/* Order details */}
          <div className="bg-white border border-slate-200 rounded-2xl p-8 mb-8 shadow-sm">
            <h2 className="text-xl font-semibold text-slate-900 mb-6">Registration Details</h2>
            
            {orderData && (
              <div className="space-y-4 mb-8">
                <div className="flex justify-between items-center py-3 border-b border-slate-100">
                  <span className="text-slate-500">Name</span>
                  <span className="text-slate-900 font-medium">
                    {orderData.first_name} {orderData.last_name}
                  </span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-slate-100">
                  <span className="text-slate-500">Email</span>
                  <span className="text-slate-900 font-medium">{orderData.email}</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-slate-100">
                  <span className="text-slate-500">Reference</span>
                  <span className="text-slate-700 font-mono text-sm">{orderData.reference}</span>
                </div>
                <div className="flex justify-between items-center py-3">
                  <span className="text-slate-500">Amount Paid</span>
                  <span className="text-[#10B981] font-bold text-lg">R{(orderData.amount / 100).toFixed(2)}</span>
                </div>
              </div>
            )}
            
            {/* Workshop info */}
            <div className="bg-slate-50 rounded-xl p-6 space-y-4">
              <h3 className="text-slate-900 font-semibold mb-4">Growth Marketing Workshop</h3>
              <div className="flex items-center gap-3 text-slate-600">
                <Calendar className="w-5 h-5 text-[#007AFF]" />
                <span>22 - 23 April 2025</span>
              </div>
              <div className="flex items-center gap-3 text-slate-600">
                <Clock className="w-5 h-5 text-[#007AFF]" />
                <span>10:00 - 12:00 (2 hours each day)</span>
              </div>
            </div>
          </div>
          
          {/* What's next */}
          <div className="bg-gradient-to-br from-[#007AFF]/5 to-[#10B981]/5 border border-[#007AFF]/20 rounded-2xl p-8 mb-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-6">What Happens Next?</h2>
            <div className="space-y-6">
              <div className="flex gap-4">
                <div className="w-8 h-8 rounded-full bg-[#007AFF] flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-sm">1</span>
                </div>
                <div>
                  <h4 className="text-slate-900 font-medium mb-1">Check Your Email</h4>
                  <p className="text-slate-600">
                    You'll receive a confirmation email with all the workshop details and your calendar invite.
                  </p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-8 h-8 rounded-full bg-[#007AFF] flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-sm">2</span>
                </div>
                <div>
                  <h4 className="text-slate-900 font-medium mb-1">Add to Calendar</h4>
                  <p className="text-slate-600">
                    Mark 22-23 April in your calendar. The workshop runs from 10:00 - 12:00 each day.
                  </p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-8 h-8 rounded-full bg-[#007AFF] flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-sm">3</span>
                </div>
                <div>
                  <h4 className="text-slate-900 font-medium mb-1">Join Live</h4>
                  <p className="text-slate-600">
                    Join the live sessions with Thato Garekoe and start building your growth marketing system.
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Host reminder */}
          <div className="bg-white border border-slate-200 rounded-2xl p-8 flex flex-col md:flex-row items-center gap-6 shadow-sm">
            <img 
              src={THATO_IMAGE} 
              alt="Thato Garekoe"
              className="w-24 h-24 rounded-full object-cover border-2 border-[#007AFF]"
            />
            <div className="text-center md:text-left">
              <p className="text-slate-500 mb-2">Your Host</p>
              <h3 className="text-xl font-semibold text-slate-900">Thato Garekoe MBA</h3>
              <p className="text-slate-600">Founder & CEO, Rekisa</p>
            </div>
          </div>
          
          {/* Back to home */}
          <div className="text-center mt-12">
            <Link to="/">
              <Button
                data-testid="back-home-btn"
                variant="outline"
                className="h-12 px-8 border-slate-300 text-slate-700 rounded-full hover:bg-slate-50"
              >
                Back to Homepage
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </main>
      
      {/* Footer */}
      <footer className="py-8 border-t border-slate-200 mt-12">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <p className="text-slate-500 text-sm">
            © 2025 Rekisa. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
